/**
 * Event log composable for real-time event tracking
 * Provides a circular buffer of events for debugging and monitoring
 */
import { ref, readonly } from "vue";

const MAX_EVENTS = 100;

export type EventType = "socket" | "status" | "recovery" | "api";

export interface DevEvent {
  id: number;
  type: EventType;
  name: string;
  payload?: any;
  timestamp: number;
}

// Singleton state
let eventIdCounter = 0;
const events = ref<DevEvent[]>([]);

// Track if event logging is enabled
const isEnabled = ref(true);

/**
 * Add a new event to the log
 */
function logEvent(type: EventType, name: string, payload?: any): DevEvent {
  const event: DevEvent = {
    id: ++eventIdCounter,
    type,
    name,
    payload,
    timestamp: Date.now(),
  };

  if (isEnabled.value) {
    events.value.push(event);

    // Keep only the last MAX_EVENTS
    if (events.value.length > MAX_EVENTS) {
      events.value = events.value.slice(-MAX_EVENTS);
    }
  }

  return event;
}

/**
 * Clear all events
 */
function clearEvents(): void {
  events.value = [];
}

/**
 * Get events filtered by type
 */
function getEventsByType(type: EventType): DevEvent[] {
  return events.value.filter((e) => e.type === type);
}

/**
 * Export events as JSON
 */
function exportEvents(): string {
  return JSON.stringify(events.value, null, 2);
}

/**
 * Enable/disable event logging
 */
function setEnabled(enabled: boolean): void {
  isEnabled.value = enabled;
}

export function useEventLog() {
  return {
    events: readonly(events),
    isEnabled: readonly(isEnabled),
    logEvent,
    clearEvents,
    getEventsByType,
    exportEvents,
    setEnabled,
  };
}

// ============================================
// Convenience logging functions for components
// ============================================

/**
 * Log a socket-related event
 */
export function logSocketEvent(name: string, payload?: any): DevEvent {
  return logEvent("socket", name, payload);
}

/**
 * Log a status change event
 */
export function logStatusEvent(name: string, payload?: any): DevEvent {
  return logEvent("status", name, payload);
}

/**
 * Log a recovery mode event
 */
export function logRecoveryEvent(name: string, payload?: any): DevEvent {
  return logEvent("recovery", name, payload);
}

/**
 * Log an API call event
 */
export function logApiEvent(name: string, payload?: any): DevEvent {
  return logEvent("api", name, payload);
}

// ============================================
// Global event logger for window access
// ============================================

// Expose to window for console access in development
if (typeof window !== "undefined") {
  (window as any).__aiAssistantEventLog = {
    getEvents: () => events.value,
    logEvent,
    clearEvents,
    exportEvents,
  };
}
